import React, { Component } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { isBrowser, isTablet } from 'react-device-detect';
import PropTypes from 'prop-types';
import {
  templateRendersAllLinesByDefault,
  HEADER,
  SUMMARY_TLDR,
  QUOTE,
  LIST,
  ACTION_ILLUSTRATION,
  EMPHASIS_STATEMENT_IMAGE,
  IMAGE_CAPTION_HORIZONTAL,
  IMAGE_CAPTION_PORTRAIT,
  SECTION_ILLUSTRATION,
  FULL_BLEED_IMAGE,
  WHY_HOW_WHAT,
  DESKTOP_WHY_HOW_WHAT,
  FIGURE_OUT_WHAT_YOU_WANT,
  CULTURE_STACK_EXPLAINED,
  ACTION_INDEX,
  PURPOSE_SUMMARY,
} from './lib/data';
import { getImageAsStyle } from './lib/images';
import List from './List';
import SummaryTLDR from './SummaryTLDR';
import WhyHowWhat from './WhyHowWhat';
import DesktopWhyHowWhat from './DesktopWhyHowWhat';
import FigureOutWhatYouWant from './FigureOutWhatYouWant';
import CultureStackExplained from './CultureStackExplained';
import ActionIndex from './ActionIndex';
import PurposeSummary from './PurposeSummary';
import './Page.css';

export default class Page extends Component {
  static propTypes = {
    invisible: PropTypes.bool,
    section: PropTypes.string,
    template: PropTypes.string,
    image: PropTypes.string,
    imagePosition: PropTypes.string,
    imageSize: PropTypes.string,
    useContentImageAsBackgroundElement: PropTypes.bool,
    lines: PropTypes.array,
    linesRendered: PropTypes.number,
    page: PropTypes.number,
    maxPage: PropTypes.number,
    onPageChange: PropTypes.func,
    transitionStatus: PropTypes.string,
  }

  state = {
    linesRendered: 1,
    leaving: false,
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    this.setRenderedLines(this.props);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  componentWillReceiveProps(nextProps) {
    this.setRenderedLines(nextProps);
  }

  setRenderedLines(props) {
    let linesRendered = 1;
    const { lines, page, previousPage, template } = props;

    if (templateRendersAllLinesByDefault(template) || previousPage > page) {
      linesRendered = lines.length;
    }

    this.setState({ linesRendered });
  }

  canRenderMoreLines() {
    const { lines } = this.props;
    const { linesRendered } = this.state;
    return lines.length > 1 && linesRendered < lines.length;
  }

  canRenderFewerLines() {
    const { lines, template } = this.props;
    const { linesRendered } = this.state;
    return lines.length > 1 &&
      linesRendered > 1 &&
      !templateRendersAllLinesByDefault(template);
  }

  handlePrevPage = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { onPageChange, page } = this.props;

    if (this.canRenderFewerLines()) {
      this.setState({ linesRendered: this.state.linesRendered - 1 });
    } else {
      this.setState({ leaving: true }, () => {
        setTimeout(() => {
          this.setState({ leaving: false })
        }, 50);
      });
      onPageChange(page - 1);
    }

    return false;
  }

  handleNextPage = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { onPageChange, page, maxPage } = this.props;
    if (page === maxPage) {
      return;
    }

    if (this.canRenderMoreLines()) {
      this.setState({ linesRendered: this.state.linesRendered + 1 });
    } else {
      this.setState({ leaving: true }, () => {
        setTimeout(() => {
          this.setState({ leaving: false })
        }, 50);
      });
      onPageChange(page + 1);
    }

    return false;
  }

  handlePageClick = (e) => {
    if (isBrowser || isTablet) {
      return;
    }

    if (e.target.tagName.toLowerCase() === 'a') {
      return true;
    }

    if ((e.pageX / window.innerWidth) >= 0.5) {
      this.handleNextPage(e);
    } else {
      this.handlePrevPage(e);
    }
  }

  handleKeyDown = (e) => {
    if (!(isBrowser || isTablet) || !!this.props.invisible) {
      return;
    }

    if (e.keyCode === 37) {
      this.handlePrevPage(e);
    } else if (e.keyCode === 39) {
      this.handleNextPage(e);
    }
  }

  renderLine(line, i) {
    const { template } = this.props;
    let lineItem = (<p key={i} dangerouslySetInnerHTML={{__html: line}} />);

    if (template === QUOTE) {
      if (i === 0) {
        lineItem = (<p key={i} dangerouslySetInnerHTML={{ __html: `&ldquo;${line}&rdquo;` }} />);
      } else if (i === 1) {
        lineItem = (<p key={i} dangerouslySetInnerHTML={{ __html: `&mdash;${line}` }} />);
      }
    }

    // return lineItem;
    return (
      <CSSTransition
        key={i}
        classNames="line"
        timeout={{ enter: i === 0 ? 0 : 500, exit: 0 }}>
        {lineItem}
      </CSSTransition>
    );
  }

  renderLines() {
    let { lines } = this.props;

    lines = lines.filter((line, i) => {
      return i <= (this.state.linesRendered - 1);
    });

    // return lines.map(this.renderLine.bind(this));
    return (
      <TransitionGroup>
        {lines.map(this.renderLine.bind(this))}
      </TransitionGroup>
    );
  }

  getBackgroundImageForDesktopWhyHowWhatLine() {
    let backgroundImage = this.props.images[this.state.linesRendered].image;
    if (backgroundImage === "") {
      backgroundImage = this.props.images[this.state.linesRendered-1].image;
    }
    return backgroundImage;
  }

  render() {
    const {
      sectionSlug,
      templateSlug,
      image,
      imagePosition,
      imageSize,
      footer,
      transitionStatus,
      hasImageContentLine } = this.props;

    const classNames = [
      "Page",
      sectionSlug,
      templateSlug,
      imagePosition,
      !!this.props.invisible ? 'invisible' : '',
      transitionStatus,
      !!this.state.leaving ? 'leaving' : '',
      footer && footer.length > 0 ? 'has-footer' : '',
      hasImageContentLine ? 'has-image-content-line' : '',
    ];

    const backgroundClassNames = [
      "background",
      !!this.props.invisible ? '' : 'visible',
      imagePosition,
    ]

    const backgroundImage = this.props.template === DESKTOP_WHY_HOW_WHAT
      ? this.getBackgroundImageForDesktopWhyHowWhatLine()
      : image;

    const style = image.length > 0
      ? getImageAsStyle(backgroundImage, (imageSize.length > 0 ? imageSize : '100%'))
      : null;

    let component = null;
    switch(this.props.template) {
      case SUMMARY_TLDR:
        component = <SummaryTLDR {...this.props} />;
        break;
      case LIST:
        component = <List {...this.props} />;
        break;
      case WHY_HOW_WHAT:
        component = <WhyHowWhat {...this.props} />;
        break;
      case DESKTOP_WHY_HOW_WHAT:
        component = (
          <DesktopWhyHowWhat {...this.props} linesRendered={this.state.linesRendered}>
            {this.renderLines()}
          </DesktopWhyHowWhat>
        );
        break;
      case FIGURE_OUT_WHAT_YOU_WANT:
        component = <FigureOutWhatYouWant {...this.props} />;
        break;
      case CULTURE_STACK_EXPLAINED:
        component = <CultureStackExplained {...this.props} />;
        break;
      case ACTION_INDEX:
        component = <ActionIndex {...this.props} />;
        break;
      case PURPOSE_SUMMARY:
        component = <PurposeSummary />;
        break;
      case ACTION_ILLUSTRATION:
      case EMPHASIS_STATEMENT_IMAGE:
      case IMAGE_CAPTION_HORIZONTAL:
      case IMAGE_CAPTION_PORTRAIT:
      case FULL_BLEED_IMAGE:
        component = <p key="1" dangerouslySetInnerHTML={{__html: this.props.lines[0]}} />;
        break;
      case SECTION_ILLUSTRATION:
        break;
      case HEADER:
        component = ([
          <p key="1" dangerouslySetInnerHTML={{__html: this.props.header}} />,
          <p key="2" dangerouslySetInnerHTML={{__html: this.props.lines[0]}} />
        ]);
        break;
      default:
        component = this.renderLines();
        break;
    }

    return (
      <section className={classNames.join(' ')} onClick={this.handlePageClick}>
        <div className="content">
          <div className="content-wrap">
            {component}
          </div>
        </div>
        <div className={backgroundClassNames.join(' ')} style={style} />
      </section>
    );
  }
}
